import { z } from "zod";
import { AdminAuditEntry } from "./admin";
import { OrderStatus, PaymentMode, QuoteKind, RefundStatus } from "./commerce";
import { Cents, Coins, IsoUtc, LedgerUnit, Uuid } from "./primitives";

/**
 * Finance staff tools (M7, ADR-0040; docs/06 section 7 `/orders`, `/financial-cases`, `/refunds`, `/ledger` under the
 * admin prefix; docs/04 sections 12-13; docs/11 section 7 "Dispute/refund"). Every route checks ONE permission on the
 * server (`requirePermission`); nothing here is authority:
 *
 *   GET  /admin/financial-cases                  finance.cases.read     ?type= &state= &minAgeHours= &cursor= &limit=
 *   GET  /admin/financial-cases/:id              finance.cases.read     case + order, payments, refunds, holds, timeline, audit
 *   POST /admin/financial-cases/:id/resolve      finance.cases.resolve  { reason } + Idempotency-Key (dangerous)
 *   GET  /admin/refunds                          finance.cases.read     ?state= &cursor= &limit=
 *   POST /admin/refunds/:id/retry                finance.refunds.retry  { reason } + Idempotency-Key (dangerous)
 *   GET  /admin/finance-alerts                   finance.cases.read     ?status=open|all &cursor= &limit=
 *   POST /admin/finance-alerts/:id/acknowledge   finance.alerts.ack     { note } + Idempotency-Key
 *   GET  /admin/orders, /admin/orders/:id        finance.ledger.read    read-only
 *   GET  /admin/ledger/users/:userId[/journal]   finance.ledger.read    credit provenance, balances, journal history
 *   GET  /admin/ledger/report, /ledger/integrity finance.ledger.read    finance report, integrity check summary
 *
 * There is deliberately NO route that edits a wallet balance, posts an arbitrary journal, grants credit or coins, or
 * changes an owner (docs/06 section 7: "No direct wallet-balance edit route"). Writes need a reason, an Idempotency-Key
 * and write one `admin_audit` row; a repeat with the same key replays the first answer.
 *
 * Units are explicit everywhere: `...Cents` fields are USD cents (cash or Townsy Credit, as named), `...Coins` fields are
 * whole Townsy Coins. They are never added together.
 */

export const FINANCE_ADMIN_DEFAULT_LIMIT = 50;
const PageQuery = {
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
};

/** Free text an operator writes for the record. Trimmed; untrusted (shown as plain text). */
const OperatorText = (min: number, max: number) => z.string().trim().min(min, `at least ${min} characters`).max(max, `at most ${max} characters`);

// ------------------------------------------------------------------------------------------ shared pieces

export const FinancialCaseType = z.enum(["DISPUTE", "REFUND", "SUPPORT"]);
export type FinancialCaseType = z.infer<typeof FinancialCaseType>;
export const FinancialCaseState = z.enum(["open", "under_review", "resolved"]);
export type FinancialCaseState = z.infer<typeof FinancialCaseState>;
export const RefundRecordStateView = z.enum(["requested", "pending", "requires_action", "succeeded", "failed", "canceled"]);
export type RefundRecordStateView = z.infer<typeof RefundRecordStateView>;
export const FinanceAlertSeverity = z.enum(["info", "warning", "critical"]);

/** An account as finance staff see it: id plus display name (null when the profile is gone). */
export const AccountRef = z.object({ userId: z.string(), label: z.string().nullable() });
export type AccountRef = z.infer<typeof AccountRef>;

export const FinancialCaseSummary = z.object({
  caseId: Uuid,
  type: FinancialCaseType,
  state: FinancialCaseState,
  orderId: Uuid.nullable(),
  account: AccountRef,
  /** Provider dispute or refund id. */
  providerReference: z.string().nullable(),
  /** USD cents under dispute / refunded outside Townsy. An unresolved case is NOT a recovered or written-off loss. */
  amountCents: Cents,
  /** True while the case is unresolved and pauses the account's new dollar orders (risk control, docs/04 section 12). */
  restrictsSpending: z.boolean(),
  createdAt: IsoUtc,
  updatedAt: IsoUtc,
  resolvedAt: IsoUtc.nullable(),
});
export type FinancialCaseSummary = z.infer<typeof FinancialCaseSummary>;

/** One order as finance staff see it (dollar or coin). Coin orders have zero dollar fields, dollar orders zero coins. */
export const AdminOrderSummary = z.object({
  orderId: Uuid,
  kind: QuoteKind,
  unit: z.enum(["USD", "COIN"]),
  status: OrderStatus,
  account: AccountRef,
  propertyId: z.string().nullable(),
  description: z.string(),
  subtotalCents: Cents,
  subtotalCoins: Coins,
  taxCents: Cents,
  /** Townsy Credit applied (USD cents of store credit, not cash). */
  creditAppliedCents: Cents,
  /** Card cash charged or to be charged (USD cents). */
  cardAmountCents: Cents,
  /** The provider's payment state, tracked separately from the order status ("none" without a payment). */
  paymentStatus: z.string(),
  /** Latest compensation refund attempt: its true state ("none" when there is no refund). */
  refundStatus: RefundStatus,
  refundAmountCents: Cents,
  provider: z.string().nullable(),
  providerPaymentId: z.string().nullable(),
  receiptNumber: z.string().nullable(),
  statusReason: z.string().nullable(),
  createdAt: IsoUtc,
  updatedAt: IsoUtc,
  fulfilledAt: IsoUtc.nullable(),
  /** Dollar orders: the Stripe mode they were placed in (ADR-0046); coin orders: null. */
  mode: PaymentMode.nullable(),
});
export type AdminOrderSummary = z.infer<typeof AdminOrderSummary>;

export const AdminPaymentView = z.object({
  provider: z.string(),
  providerPaymentId: z.string(),
  state: z.string(),
  amountCents: Cents,
  amountReceivedCents: Cents,
  currency: z.string(),
  latestChargeId: z.string().nullable(),
  succeededAt: IsoUtc.nullable(),
  lastSyncedAt: IsoUtc,
  createdAt: IsoUtc,
});
export type AdminPaymentView = z.infer<typeof AdminPaymentView>;

/** One compensation refund attempt (refund_records). A pending or failed refund is never shown as refunded. */
export const AdminRefundView = z.object({
  refundId: Uuid,
  orderId: Uuid,
  account: AccountRef,
  orderStatus: OrderStatus,
  providerPaymentId: z.string(),
  /** 1 for the first refund of the order; a deliberate retry after a failure is 2, 3, ... (provider key refund:<order>:<n>). */
  attempt: z.number().int().min(1),
  reason: z.string(),
  amountCents: Cents,
  currency: z.string(),
  providerRefundId: z.string().nullable(),
  state: RefundRecordStateView,
  /** How many times the worker (or the retry route) tried to submit it to the provider. */
  submissionAttempts: z.number().int().min(0),
  lastError: z.string().nullable(),
  failureReason: z.string().nullable(),
  submittedAt: IsoUtc.nullable(),
  succeededAt: IsoUtc.nullable(),
  createdAt: IsoUtc,
  updatedAt: IsoUtc,
  /** This is the order's newest attempt. */
  latest: z.boolean(),
  /** The newest attempt failed or was canceled: an operator may retry it (finance.refunds.retry). */
  retryable: z.boolean(),
});
export type AdminRefundView = z.infer<typeof AdminRefundView>;

/** The order's Townsy Credit hold (wallet_holds): HELD, then CONSUMED or RELEASED exactly once. */
export const AdminHoldView = z.object({
  holdId: Uuid,
  amountCents: Cents,
  state: z.enum(["HELD", "CONSUMED", "RELEASED"]),
  createdAt: IsoUtc,
  resolvedAt: IsoUtc.nullable(),
  holdJournalId: Uuid,
  consumeJournalId: Uuid.nullable(),
  releaseJournalId: Uuid.nullable(),
});
export type AdminHoldView = z.infer<typeof AdminHoldView>;

/** The single terminal fulfilment (order_fulfillments) and its cash/credit receipt mapping. */
export const AdminFulfillmentView = z.object({
  fulfillmentId: Uuid,
  kind: QuoteKind,
  propertyId: z.string().nullable(),
  newIntervalId: Uuid.nullable(),
  displacedIntervalId: Uuid.nullable(),
  displacedOwner: AccountRef.nullable(),
  /** Townsy Credit issued to the displaced owner: 100% of the displaced interval's basis (never cash). */
  displacementCreditCents: Cents,
  displacementJournalId: Uuid.nullable(),
  creditSpendJournalId: Uuid.nullable(),
  creditCents: Cents,
  cardCents: Cents,
  basisCents: Cents.nullable(),
  nextPriceCents: Cents.nullable(),
  createdAt: IsoUtc,
});
export type AdminFulfillmentView = z.infer<typeof AdminFulfillmentView>;

/** What happened downstream of the ownership this order bought (F16: later owners are never unwound automatically). */
export const AdminOwnershipView = z.object({
  intervalId: Uuid,
  propertyId: z.string(),
  owner: AccountRef,
  acquiredAt: IsoUtc,
  endedAt: IsoUtc.nullable(),
  /** The interval that replaced it (a later takeover), if any. */
  replacedByIntervalId: Uuid.nullable(),
  stillCurrent: z.boolean(),
  /** The property's owner today (may be a later buyer). */
  currentOwner: AccountRef.nullable(),
});
export type AdminOwnershipView = z.infer<typeof AdminOwnershipView>;

/** A provider event in the durable inbox (payload left out). */
export const AdminProviderEventView = z.object({
  providerEventId: z.string(),
  type: z.string(),
  source: z.string(),
  state: z.string(),
  outcome: z.string().nullable(),
  attempts: z.number().int().min(0),
  lastError: z.string().nullable(),
  eventCreatedAt: IsoUtc,
  receivedAt: IsoUtc,
  processedAt: IsoUtc.nullable(),
});
export type AdminProviderEventView = z.infer<typeof AdminProviderEventView>;

export const FinanceAlertView = z.object({
  alertId: Uuid,
  kind: z.string(),
  severity: FinanceAlertSeverity,
  orderId: Uuid.nullable(),
  providerReference: z.string().nullable(),
  message: z.string(),
  /** Structured details as raised (amounts in cents, ids). Untrusted text: shown as plain text. */
  details: z.record(z.string(), z.unknown()),
  createdAt: IsoUtc,
  acknowledgedAt: IsoUtc.nullable(),
  acknowledgedBy: AccountRef.nullable(),
  /** The note written when it was acknowledged (from the audit trail). */
  acknowledgementNote: z.string().nullable(),
});
export type FinanceAlertView = z.infer<typeof FinanceAlertView>;

export const TimelineEntry = z.object({
  at: IsoUtc,
  kind: z.enum(["order", "hold", "payment", "fulfillment", "refund", "case", "event", "alert", "audit"]),
  label: z.string(),
  ref: z.string().nullable(),
});
export type TimelineEntry = z.infer<typeof TimelineEntry>;

// ------------------------------------------------------------------------------------------ orders (read-only)

export const AdminOrderListQuery = z.object({
  status: OrderStatus.optional(),
  kind: QuoteKind.optional(),
  unit: z.enum(["USD", "COIN"]).optional(),
  userId: z.string().trim().min(1).max(200).optional(),
  ...PageQuery,
});
export type AdminOrderListQuery = z.infer<typeof AdminOrderListQuery>;
export const AdminOrderListResponse = z.object({ items: z.array(AdminOrderSummary), nextCursor: z.string().nullable() });
export type AdminOrderListResponse = z.infer<typeof AdminOrderListResponse>;

/** ADR-0046: one Stripe Tax record of an order: the sale (recorded after fulfilment) or a reversal (after a refund). */
export const AdminTaxTransactionView = z.object({
  id: Uuid,
  kind: z.enum(["sale", "reversal"]),
  state: z.enum(["pending", "recorded", "failed"]),
  mode: PaymentMode,
  calculationId: z.string().nullable(),
  reference: z.string(),
  providerTransactionId: z.string().nullable(),
  /** Sale: price + tax; reversal: the gross amount reversed. */
  amountCents: Cents,
  /** Sale: the tax; reversal: the tax reversed (null until the provider reports it). */
  taxCents: Cents.nullable(),
  providerRefundId: z.string().nullable(),
  attempts: z.number().int().min(0),
  lastError: z.string().nullable(),
  recordedAt: IsoUtc.nullable(),
  createdAt: IsoUtc,
});
export type AdminTaxTransactionView = z.infer<typeof AdminTaxTransactionView>;

export const AdminOrderDetail = z.object({
  order: AdminOrderSummary.extend({
    quoteId: Uuid.nullable(),
    reservationExpiresAt: IsoUtc.nullable(),
    expectedOwner: AccountRef.nullable(),
    expectedIntervalId: Uuid.nullable(),
    reimbursableBasisCents: Cents.nullable(),
    expectedDisplacementCreditCents: Cents.nullable(),
    pricingPolicyVersion: z.number().int().nullable(),
    termsVersion: z.string().nullable(),
    campaignId: Uuid.nullable(),
    itemId: z.string().nullable(),
  }),
  payments: z.array(AdminPaymentView),
  refunds: z.array(AdminRefundView),
  holds: z.array(AdminHoldView),
  fulfillment: AdminFulfillmentView.nullable(),
  /** ADR-0046: the Stripe Tax sale transaction and any reversals of this order (empty without a tax calculation). */
  taxTransactions: z.array(AdminTaxTransactionView).optional(),
  ownership: AdminOwnershipView.nullable(),
  cases: z.array(FinancialCaseSummary),
  alerts: z.array(FinanceAlertView),
  providerEvents: z.array(AdminProviderEventView),
  audit: z.array(AdminAuditEntry),
  timeline: z.array(TimelineEntry),
});
export type AdminOrderDetail = z.infer<typeof AdminOrderDetail>;

// ------------------------------------------------------------------------------------------ financial cases

export const FinancialCaseListQuery = z.object({
  type: FinancialCaseType.optional(),
  /** "unresolved" = open or under review. */
  state: z.enum(["open", "under_review", "resolved", "unresolved"]).optional(),
  /** Only cases opened at least this many hours ago. */
  minAgeHours: z.coerce.number().int().min(1).max(24 * 366).optional(),
  ...PageQuery,
});
export type FinancialCaseListQuery = z.infer<typeof FinancialCaseListQuery>;
export const FinancialCaseListResponse = z.object({ items: z.array(FinancialCaseSummary), nextCursor: z.string().nullable() });
export type FinancialCaseListResponse = z.infer<typeof FinancialCaseListResponse>;

export const FinancialCaseDetail = z.object({
  case: FinancialCaseSummary.extend({
    /** Evidence captured when the case opened (order funding, fulfilment, intervals, journals). */
    evidence: z.record(z.string(), z.unknown()),
    resolutionPlan: z.string().nullable(),
  }),
  order: AdminOrderDetail.nullable(),
  /** Every DISPUTE case on the same order (this one included when it is a dispute). */
  disputes: z.array(FinancialCaseSummary),
  /** Audit rows about this case or its order, newest first. */
  audit: z.array(AdminAuditEntry),
  timeline: z.array(TimelineEntry),
});
export type FinancialCaseDetail = z.infer<typeof FinancialCaseDetail>;

/** POST /admin/financial-cases/:id/resolve. The reason is the approved plan; it is stored on the case and in the audit. */
export const FinancialCaseResolveRequest = z.object({ reason: OperatorText(3, 2000) }).strict();
export type FinancialCaseResolveRequest = z.infer<typeof FinancialCaseResolveRequest>;
export const FinancialCaseResolveResponse = z.object({ case: FinancialCaseSummary });
export type FinancialCaseResolveResponse = z.infer<typeof FinancialCaseResolveResponse>;

// ------------------------------------------------------------------------------------------ refunds

export const AdminRefundListQuery = z.object({ state: RefundRecordStateView.optional(), ...PageQuery });
export type AdminRefundListQuery = z.infer<typeof AdminRefundListQuery>;
export const AdminRefundListResponse = z.object({ items: z.array(AdminRefundView), nextCursor: z.string().nullable() });
export type AdminRefundListResponse = z.infer<typeof AdminRefundListResponse>;

/** POST /admin/refunds/:id/retry: `:id` is the failed/canceled attempt the operator looked at (must be the newest). */
export const RefundRetryRequest = z.object({ reason: OperatorText(3, 500) }).strict();
export type RefundRetryRequest = z.infer<typeof RefundRetryRequest>;
export const RefundRetryResponse = z.object({
  /** The new attempt, in the state the provider reported (or "requested" when it was not submitted from here). */
  refund: AdminRefundView,
  order: AdminOrderSummary,
  /** The provider accepted the submission in this request. False: it stays "requested" and the worker submits it. */
  submitted: z.boolean(),
  /** Plain explanation for the operator (e.g. why it was not submitted, the provider's refusal). */
  note: z.string(),
});
export type RefundRetryResponse = z.infer<typeof RefundRetryResponse>;

// ------------------------------------------------------------------------------------------ alerts

export const FinanceAlertListQuery = z.object({ status: z.enum(["open", "all"]).optional(), ...PageQuery });
export type FinanceAlertListQuery = z.infer<typeof FinanceAlertListQuery>;
export const FinanceAlertListResponse = z.object({ items: z.array(FinanceAlertView), nextCursor: z.string().nullable() });
export type FinanceAlertListResponse = z.infer<typeof FinanceAlertListResponse>;

export const FinanceAlertAckRequest = z.object({ note: OperatorText(3, 500) }).strict();
export type FinanceAlertAckRequest = z.infer<typeof FinanceAlertAckRequest>;
export const FinanceAlertAckResponse = z.object({ alert: FinanceAlertView });
export type FinanceAlertAckResponse = z.infer<typeof FinanceAlertAckResponse>;

// ------------------------------------------------------------------------------------------ ledger (read-only)

export const CreditGrantView = z.object({
  journalId: Uuid,
  type: z.string(),
  amountCents: Cents,
  at: IsoUtc,
  propertyId: z.string().nullable(),
  /** DISPLACEMENT_CREDIT: the interval that was displaced, the order that displaced it and the order that had bought it. */
  displacedIntervalId: Uuid.nullable(),
  displacingOrderId: Uuid.nullable(),
  displacedAcquisitionOrderId: Uuid.nullable(),
});
export type CreditGrantView = z.infer<typeof CreditGrantView>;
export const CreditUseView = z.object({ orderId: Uuid, kind: z.string(), amountCents: Cents, state: z.string(), at: IsoUtc });
export type CreditUseView = z.infer<typeof CreditUseView>;

export const LedgerAccountResponse = z.object({
  account: AccountRef.extend({ status: z.string().nullable() }),
  /** Current projections. Coins and Townsy Credit are separate ledgers; neither is cash. */
  coins: z.object({ available: Coins, reserved: Coins }),
  credit: z.object({ availableCents: Cents, reservedCents: Cents }),
  /** Where the account's Townsy Credit came from and where it went (docs/05 credit provenance). */
  provenance: z.object({ grants: z.array(CreditGrantView), uses: z.array(CreditUseView), truncated: z.boolean() }),
  cases: z.array(FinancialCaseSummary),
  /** An unresolved case pauses the account's new dollar orders. */
  spendingRestricted: z.boolean(),
});
export type LedgerAccountResponse = z.infer<typeof LedgerAccountResponse>;

export const LedgerJournalQuery = z.object({ unit: LedgerUnit, ...PageQuery });
export type LedgerJournalQuery = z.infer<typeof LedgerJournalQuery>;
export const LedgerJournalItem = z.object({
  journalId: Uuid,
  unit: LedgerUnit,
  type: z.string(),
  reason: z.string(),
  businessEventKey: z.string(),
  orderId: Uuid.nullable(),
  propertyId: z.string().nullable(),
  actorUserId: z.string().nullable(),
  reversalOf: Uuid.nullable(),
  at: IsoUtc,
  /** Signed change to the account's AVAILABLE balance in the unit (coins or credit cents); 0 when untouched. */
  availableDelta: z.number().int(),
  /** Signed change to its RESERVED balance (credit holds); 0 when untouched. */
  reservedDelta: z.number().int(),
});
export type LedgerJournalItem = z.infer<typeof LedgerJournalItem>;
export const LedgerJournalResponse = z.object({ unit: LedgerUnit, items: z.array(LedgerJournalItem), nextCursor: z.string().nullable() });
export type LedgerJournalResponse = z.infer<typeof LedgerJournalResponse>;

/** docs/04 sections 1 and 4: card cash, gross volume and outstanding credit are three numbers, never summed. */
export const FinanceReportResponse = z.object({
  /** The server's Stripe mode; cash, volume and tax below count only orders and payments of this mode (ADR-0046). */
  mode: PaymentMode,
  cash: z.object({
    collectedNetCents: z.number().int(),
    settledGrossCents: Cents,
    settledPaymentCount: z.number().int().min(0),
    refundedCents: Cents,
    refundsInProgressCents: Cents,
    refundsFailedCents: Cents,
  }),
  /**
   * ADR-0046: sales tax, a fourth separate number. Collected = tax of fulfilled orders (always card-paid; it is part of
   * the card cash above but is owed to the tax authorities, not Townsy's money). Recorded = reported to Stripe Tax.
   */
  tax: z
    .object({
      collectedCents: Cents,
      taxedOrderCount: z.number().int().min(0),
      recordedCents: Cents,
      pendingRecordingCents: Cents,
      pendingRecordingCount: z.number().int().min(0),
      failedRecordingCount: z.number().int().min(0),
      reversedCents: Cents,
    })
    .optional(),
  /** Payments of the OTHER mode in this database (e.g. test payments made before going live): never real revenue here. */
  otherMode: z
    .object({
      mode: PaymentMode,
      settledGrossCents: Cents,
      settledPaymentCount: z.number().int().min(0),
      refundedCents: Cents,
      fulfilledOrderCount: z.number().int().min(0),
    })
    .optional(),
  /**
   * ADR-0046 (escalated, founder decision): what test-mode purchases still affect a live server. Buildings held by a
   * test-mode purchase carry a basis that a live takeover would reimburse as REAL Townsy Credit; credit granted by
   * test-mode takeovers is spendable on live purchases. All zero on a fresh live database.
   */
  liveReadiness: z
    .object({
      testModeOwnedProperties: z.number().int().min(0),
      testModeOwnedBasisCents: Cents,
      testModeDisplacementCreditCents: Cents,
      openTestModeOrders: z.number().int().min(0),
    })
    .optional(),
  volume: z.object({
    grossAcquisitionVolumeCents: Cents,
    acquisitionCount: z.number().int().min(0),
    fulfilledByKindCents: z.record(QuoteKind, Cents),
    creditAppliedCents: Cents,
    cardAppliedCents: Cents,
  }),
  credit: z.object({
    outstandingCents: Cents,
    outstandingAvailableCents: Cents,
    outstandingReservedCents: Cents,
    issuedByDisplacementCents: Cents,
    issuedByOperatorCents: Cents,
    issuedByDevFixturesCents: Cents,
    spentCents: Cents,
    forfeitedCents: Cents,
  }),
  risk: z.object({
    openDisputes: z.number().int().min(0),
    openDisputeCents: Cents,
    openCases: z.number().int().min(0),
    openAlerts: z.number().int().min(0),
  }),
  asOf: IsoUtc,
});
export type FinanceReportResponse = z.infer<typeof FinanceReportResponse>;

const Count = z.number().int().min(0);

/** One UTC day of the staff Overview (GET /admin/overview). */
export const AdminOverviewDay = z.object({
  /** "YYYY-MM-DD" (UTC). */
  day: z.string(),
  /** Card payments of the server's Stripe mode that succeeded that day, before refunds. */
  cardReceivedCents: Cents,
  /** Refunds of those payments that succeeded that day. */
  refundedCents: Cents,
  /** Accounts created that day that still exist (Townsy's own and deleted accounts excluded). */
  newAccounts: Count,
});
export type AdminOverviewDay = z.infer<typeof AdminOverviewDay>;

/**
 * Founder 2026-10-28: "a 'finance' tab ... total revenue, total users, etc". What the staff Overview adds to the finance
 * report (its money numbers come from GET /admin/ledger/report, unchanged): counts and UTC days, no personal data.
 */
export const AdminOverviewResponse = z.object({
  asOf: IsoUtc,
  mode: PaymentMode,
  accounts: z.object({ total: Count, verified: Count, newToday: Count, newLast7Days: Count, newLast30Days: Count }),
  properties: z.object({ storesTotal: Count, storesOwnedByMembers: Count, storeOwners: Count, plotsTotal: Count, plotsOwnedByMembers: Count }),
  orders: z.object({ fulfilled: Count, fulfilledLast7Days: Count }),
  /**
   * ADR-0051: members' coins and where they came from, never blended: earned from sponsored tasks, granted by Townsy
   * (audited operator grants), development fixtures (0 in production); spent on plots, home upgrades and items.
   */
  coins: z.object({ held: Count, earned: Count, grantedByTownsy: Count, devFixtures: Count, spent: Count }),
  /** Distinct visitors (the public statistics' definition: test and staff traffic excluded), UTC today and the last 7 UTC days; null = not measured. */
  visitors: z.object({ today: Count.nullable(), last7Days: Count.nullable() }),
  /** The last 30 UTC days, oldest first; today, still running, last. */
  days: z.array(AdminOverviewDay),
});
export type AdminOverviewResponse = z.infer<typeof AdminOverviewResponse>;

/** The read-only integrity check (docs/04 section 13 invariants in one snapshot). */
export const IntegritySummaryResponse = z.object({
  checkedAt: IsoUtc,
  ok: z.boolean(),
  violations: z.array(z.string()),
  /** ADR-0046: not violations but founder decisions (live mode: test-mode purchases that still matter). */
  warnings: z.array(z.string()).optional(),
  /** Exact row counts per table (compare a restored copy with its source). */
  counts: z.record(z.string(), z.number().int().min(0)),
});
export type IntegritySummaryResponse = z.infer<typeof IntegritySummaryResponse>;
